import React, { Dispatch, useCallback, useEffect, useMemo } from 'react';
import { Badge, IconButton, Stack, SxProps } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IAppReduxStore } from '../../../redux/store';
import { vars } from '../../../Theme';
import Icon from '@mdi/react';
import { mdiFilterOutline, mdiViewColumnOutline } from '@mdi/js';
import { setShowColumnsMenu, setShowFilter } from '../../../redux/user/sliceUserSettings';
import ViewControls from '../ViewControls';
import ViewControlsButton from '../ViewControlsButton';
import { setCurrentView } from '../../../redux/import/sliceImportData';
import ProjectAreaIndicator from '../ProjectView/ProjectAreaIndicator';
import { useParams } from 'react-router-dom';
import {
    fetchImportDocTypes,
    fetchImportViewData,
    fetchImportViews,
} from '../../../util/ImportUtils';
import RefreshButton from '../../General/RefreshButton/RefreshButton';
import ProjectCreationActionsMenu from '../ProjectView/ProjectCreationActionsMenu';
import ScrollableTabs from '../../General/ScrollableTabs/ScrollableTabs';

const iconButtonSx: SxProps = {
    width: '34px',
    height: '34px',
    borderRadius: '5px',
    minWidth: '34px',
    padding: '0',
    border: `1px solid ${vars.lightGray}`,
    '&:hover': {
        backgroundColor: vars.fillGray,
    },
};

/**
 * Component that allows the user to change how their projects are displayed.
 */
export default () => {
    /** Configure component translation. */
    const { i18n, t } = useTranslation('translations', { keyPrefix: 'importViewControls' });

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get relevant values from Redux store. */
    const currentView = useSelector((store: IAppReduxStore) => store.importData.currentView);
    const importViews = useSelector((store: IAppReduxStore) => store.importViews.data);
    const { showFilter, showColumnsMenu, hasFilteredItems } = useSelector(
        (store: IAppReduxStore) => store.userSettings,
    );

    const documentsLoading = useSelector(
        (_store: IAppReduxStore) => _store.importDocuments.loading,
    );
    const viewsLoading = useSelector((store: IAppReduxStore) => store.importViews.loading);
    const viewDataFieldsLoading = useSelector(
        (store: IAppReduxStore) => store.importViewData.fields.loading,
    );
    const viewDataFieldSchemaLoading = useSelector(
        (store: IAppReduxStore) => store.importViewData.fieldSchema.loading,
    );

    const documentTypesLoading = useSelector(
        (store: IAppReduxStore) => store.importDocTypes.loading,
    );

    useEffect(() => {
        if (importViews.length && !currentView) {
            dispatch(setCurrentView(importViews[0].name));
        }
    }, [importViews]);

    /**
     * Toggles the visibility of the data grid's columns menu.
     */
    const toggleShowColumnsMenu = useCallback(() => {
        dispatch(setShowColumnsMenu(!showColumnsMenu));
    }, [showColumnsMenu]);

    const toggleShowFilter = useCallback(() => {
        dispatch(setShowFilter(!showFilter));
    }, [showFilter]);

    const onViewClick = (clickedView: string) => {
        dispatch(setCurrentView(clickedView));
    };

    const viewButtons = useMemo(
        () =>
            (importViews ?? []).map(x => (
                <ViewControlsButton
                    label={t(x.name.toLowerCase())}
                    key={x.name}
                    active={x.name === currentView}
                    onClick={() => onViewClick(x.name)}
                />
            )),
        [importViews, currentView, i18n.language],
    );

    if (!importViews) {
        return <></>;
    }

    return (
        <ViewControls
            header={<ProjectAreaIndicator />}
            row1={<ProjectCreationActionsMenu />}
            row2={
                <Stack
                    direction='row'
                    sx={{
                        flexGrow: 4,
                        alignItems: 'center',
                        p: 1,
                    }}
                >
                    <ScrollableTabs>{viewButtons}</ScrollableTabs>
                    <Stack
                        direction='row'
                        spacing={1}
                        sx={{
                            flexGrow: 0,
                        }}
                    >
                        <IconButton sx={iconButtonSx} onClick={toggleShowColumnsMenu}>
                            <Icon path={mdiViewColumnOutline} size={1} color={vars.font} />
                        </IconButton>
                        <RefreshButton
                            handleOnClick={() => {
                                fetchImportViews(replicaId);
                                fetchImportViewData(replicaId);
                                fetchImportDocTypes(replicaId);
                            }}
                            isLoading={
                                documentsLoading ||
                                viewsLoading ||
                                viewDataFieldsLoading ||
                                viewDataFieldSchemaLoading ||
                                documentTypesLoading
                            }
                        />
                        <IconButton sx={iconButtonSx} onClick={toggleShowFilter}>
                            <Badge
                                sx={{
                                    '& .MuiBadge-badge': { backgroundColor: `${vars.brandGreen}` },
                                }}
                                invisible={!hasFilteredItems}
                                variant='dot'
                            >
                                <Icon
                                    path={mdiFilterOutline}
                                    size={1}
                                    color={hasFilteredItems ? vars.brandGreen : vars.font}
                                />
                            </Badge>
                        </IconButton>
                    </Stack>
                </Stack>
            }
        />
    );
};
